import { useState, useEffect } from 'react';

import CustomModal from '@/components/CustomModal';
import { RadioButtonGroup } from '@/components/RadioButton';
import Select from '@/components/Select';
import { STATUS_OPTIONS } from '@/constants/constant';
import { ERoleAccess } from '@/enum';
import useAccess from '@/hooks/useAccess';
import { useFormatText } from '@/hooks/useFormatText';
import { getClassDropdown, getTeacherDropdown } from '@/service/commonService';
import { IChildrenSchoolFilterValues } from '@/types';

const ChildrenFilterModal = ({
  setIsOpen,
  onApply,
  initialValues = {},
  schoolOptions,
}: {
  setIsOpen: (open: boolean) => void;
  onApply: (filters: IChildrenSchoolFilterValues) => void;
  initialValues?: Partial<IChildrenSchoolFilterValues>;
  schoolOptions: { value: string; label: string }[];
  classOptions?: { value: string; label: string }[];
  teacherOptions?: { value: string; label: string }[];
}) => {
  const { hasAccess } = useAccess();

  const { formatText } = useFormatText();
  const [status, setStatus] = useState(initialValues.status || '');
  const [schoolId, setSchoolId] = useState<string[]>(
    initialValues.school_id || []
  );
  const [classId, setClassId] = useState<string[]>(
    initialValues.class_id || []
  );
  const [teacherId, setTeacherId] = useState<string[]>(
    initialValues.teacher_id || []
  );
  const [classOptions, setClassOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [teacherOptions, setTeacherOptions] = useState<
    { value: string; label: string }[]
  >([]);

  useEffect(() => {
    const fetchClasses = async () => {
      if (schoolId?.length > 0) {
        const classRes = await getClassDropdown({ school_id: schoolId });

        if (classRes?.status) {
          const options =
            classRes?.data?.map((item: { id: string; name: string }) => ({
              value: item?.id,
              label: item?.name,
            })) || [];
          setClassOptions(options);

          // Clear class selections if they're no longer in the options
          setClassId((prev) =>
            prev?.filter((id) =>
              options?.some(
                (opt: { value: string; label: string }) => opt.value === id
              )
            )
          );
        }
      } else {
        setClassOptions([]);
        setClassId([]);
      }
    };

    fetchClasses();
  }, [schoolId]);

  useEffect(() => {
    const fetchTeachers = async () => {
      if (classId?.length > 0) {
        const teacherRes = await getTeacherDropdown({
          class_id: classId,
        });

        if (teacherRes?.status) {
          const options =
            teacherRes?.data?.map((item: { id: string; name: string }) => ({
              value: item?.id,
              label: item?.name,
            })) || [];
          setTeacherOptions(options);

          // Clear teacher selections if they're no longer in the options
          setTeacherId((prev) =>
            prev?.filter((id) =>
              options?.some(
                (opt: { value: string; label: string }) => opt.value === id
              )
            )
          );
        }
      } else {
        setTeacherOptions([]);
        setTeacherId([]);
      }
    };

    fetchTeachers();
  }, [classId]);

  const handleClear = () => {
    setStatus('');
    setSchoolId([]);
    setClassId([]);
    setTeacherId([]);
    onApply({ status: '', school_id: [], class_id: [], teacher_id: [] });
    setIsOpen(false);
  };

  const handleApply = () => {
    const filters = {
      status,
      school_id: schoolId,
      class_id: classId,
      teacher_id: teacherId,
    };
    onApply(filters);
    setIsOpen(false);
  };
  if (!hasAccess(ERoleAccess.USER_MANAGEMENT)) return null;

  return (
    <CustomModal
      setIsOpen={setIsOpen}
      isHeader
      isFooter
      heading={formatText('common.actions.filter')}
      position="right"
      className="max-w-150"
      confirmButtonProps={{
        label: formatText('common.actions.show-results'),
        variant: 'primary',
        onClick: handleApply,
        disabled:
          !status &&
          schoolId?.length === 0 &&
          classId?.length === 0 &&
          teacherId?.length === 0,
      }}
      cancelButtonProps={{
        label: formatText('common.actions.clear-filters'),
        desructive: true,
        onClick: handleClear,
        className: 'text-sm underline px-0!',
      }}
    >
      <div className="flex flex-col gap-6 w-full">
        {/* Status */}
        <RadioButtonGroup
          name="status"
          label={formatText('user-management.status')}
          options={STATUS_OPTIONS}
          value={status}
          onChange={setStatus}
          formatLabel={formatText}
        />

        <Select
          name={formatText('children.school-subscriber.filters.all-schools')}
          options={schoolOptions}
          selected={schoolId}
          onChange={(value) => setSchoolId(Array.isArray(value) ? value : [])}
          placeholder={formatText('common.select')}
          isMulti={true}
          isClearable={true}
          isTextFormat={true}
          className="min-h-13"
        />

        <Select
          name={formatText('children.school-subscriber.filters.assigned-class')}
          options={classOptions}
          selected={classId}
          onChange={(value) => setClassId(Array.isArray(value) ? value : [])}
          placeholder={formatText('common.select')}
          isMulti={true}
          isClearable={true}
          isTextFormat={true}
          className="min-h-13"
          disabled={schoolId?.length === 0}
        />

        <Select
          name={formatText('children.school-subscriber.filters.class-teacher')}
          options={teacherOptions}
          selected={teacherId}
          onChange={(value) => setTeacherId(Array.isArray(value) ? value : [])}
          placeholder={formatText('common.select')}
          isMulti={true}
          isClearable={true}
          isTextFormat={true}
          className="min-h-13"
          disabled={classId?.length === 0}
        />
      </div>
    </CustomModal>
  );
};

export default ChildrenFilterModal;
